import { useEffect } from 'react';
import { isAgentsHubHost, tenantSlugFromPathOrHost } from '../utils/tenantDomains';

/**
 * Keeps the <link rel="manifest"> in sync with the tenant slug in the URL.
 *
 * - Root route (/, /auth, /admin, ...) → platform manifest (/manifest.json).
 * - Tenant route (/:slug/app, /:slug/auth, etc.) → tenant-branded manifest
 *   served by the backend at /api/tenants/public/:slug/manifest.json, so
 *   the installed home-screen icon shows THAT tenant's name + logo.
 *
 * Re-runs on every path change (listens to popstate + pushstate hooks).
 * Returns null — purely a side-effect component.
 */

function extractSlug(pathname) {
  return tenantSlugFromPathOrHost(pathname);
}

function setManifestHref(href) {
  let link = document.querySelector('link[rel="manifest"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'manifest';
    document.head.appendChild(link);
  }
  if (link.getAttribute('href') !== href) {
    link.setAttribute('href', href);
  }
}

function setHeadLink(rel, href, attrs = {}) {
  if (!href) return;
  let link = document.querySelector(`link[rel="${rel}"][data-tenant-install="true"]`)
    || document.querySelector(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    link.dataset.tenantInstall = 'true';
    document.head.appendChild(link);
  }
  Object.entries(attrs).forEach(([key, value]) => {
    if (value) link.setAttribute(key, value);
  });
  if (link.getAttribute('href') !== href) {
    link.setAttribute('href', href);
  }
}

const KNOWN_TENANT_BRANDS = {
  mystical: 'MYSTICAL REEL GEMS',
  starlight: 'STARLIGHT',
  startlight: 'STARLIGHT',
};

const SO_AGENT_HOSTS = new Set(['soagent.org', 'www.soagent.org', 'login.soagent.org']);
const SO_AGENT_ICON_VERSION = '20260820';
const SO_AGENT_BRAND = {
  name: 'SO Agent',
  logo: `/assets/so-agents/favicon-64.png?v=${SO_AGENT_ICON_VERSION}`,
  color: '#0b3e30',
  shareTitle: 'SO Agent',
  description: 'Manage customers, referrals, reports, earnings, and support in one secure partner workspace.',
  shareImage: `/assets/so-agents/icon-512.png?v=${SO_AGENT_ICON_VERSION}`,
  shareUrl: 'https://soagent.org/',
};

const AGENTS_HUB_BRAND = {
  ...SO_AGENT_BRAND,
  name: 'AgentHub',
  shareTitle: 'AgentHub',
  description: 'Secure agent, tenant, commission, conversation, and audit operations.',
  shareUrl: '',
};

function isSoAgentHost() {
  return typeof window !== 'undefined' && SO_AGENT_HOSTS.has(String(window.location.hostname || '').toLowerCase());
}

function isAgentsHubSurface() {
  if (typeof window === 'undefined') return false;
  if (isAgentsHubHost()) return true;
  const host = String(window.location.hostname || '').toLowerCase();
  return ['localhost', '127.0.0.1'].includes(host)
    && /^\/agents-hub(?:\/|$)/i.test(window.location.pathname);
}

function setMeta(name, content) {
  if (!content) return;
  let meta = document.querySelector(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', name);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

function setPropertyMeta(property, content) {
  if (!content) return;
  let meta = document.querySelector(`meta[property="${property}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('property', property);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
}

function titleFromSlug(slug) {
  const key = String(slug || '').toLowerCase();
  if (KNOWN_TENANT_BRANDS[key]) return KNOWN_TENANT_BRANDS[key];
  return key
    .split('-')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ') || 'TeamLobby';
}

function withCacheKey(url, key) {
  if (!url || url.startsWith('data:')) return url;
  const joiner = url.includes('?') ? '&' : '?';
  return `${url}${joiner}v=${encodeURIComponent(key)}`;
}

function pickTenantBrand(tenant, slug) {
  const branding = tenant?.branding || {};
  const theme = branding.theme || {};
  const homeScreen = branding.home_screen || {};
  const name = String(
    homeScreen.app_name ||
    theme.brand_name ||
    branding.brand_name ||
    tenant?.name ||
    slug ||
    'TeamLobby'
  ).trim();
  const logo = String(
    homeScreen.icon_url ||
    theme.logo_url ||
    theme.intro_logo_url ||
    branding.logo_url ||
    installIconHrefForSlug(slug)
  ).trim();
  const color = String(
    theme.btn_primary_from ||
    theme.neon ||
    branding.theme_color ||
    branding.primary_color ||
    '#0B0E14'
  ).trim();
  const updated = homeScreen.updated_at || branding.theme_updated_at || branding.updated_at || tenant?.updated_at || slug || 'brand';
  const shareTitle = String(homeScreen.share_title || name).trim();
  const description = String(
    homeScreen.share_bio
    || homeScreen.install_bio
    || branding.description
    || `${name} player portal.`
  ).trim();
  const shareImage = String(
    homeScreen.share_image_url
    || branding.share_image_url
    || logo
  ).trim();
  const configuredShareUrl = String(homeScreen.share_url || '').trim();
  const shareUrl = /^https?:\/\//i.test(configuredShareUrl) ? configuredShareUrl : '';
  return {
    name,
    logo: withCacheKey(logo, updated),
    color,
    shareTitle,
    description,
    shareImage: withCacheKey(shareImage, updated),
    shareUrl,
  };
}

async function fetchTenantBrand(slug, signal) {
  const res = await fetch(`/api/tenants/public/${encodeURIComponent(slug)}`, {
    credentials: 'omit',
    signal,
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error('Tenant brand fetch failed');
  return res.json();
}

function applyInstallBrand({ name, logo, color, shareTitle, description, shareImage, shareUrl }) {
  document.title = name;
  const summary = description || `${name} player portal.`;
  const socialTitle = shareTitle || name;
  setMeta('application-name', name);
  setMeta('apple-mobile-web-app-title', name);
  setMeta('theme-color', color);
  setMeta('description', summary);
  setMeta('apple-mobile-web-app-capable', 'yes');
  setMeta('mobile-web-app-capable', 'yes');
  setPropertyMeta('og:site_name', name);
  setPropertyMeta('og:title', socialTitle);
  setPropertyMeta('og:description', summary);
  setPropertyMeta('og:image', shareImage || logo);
  setPropertyMeta('og:url', shareUrl);
  setMeta('twitter:card', 'summary_large_image');
  setMeta('twitter:title', socialTitle);
  setMeta('twitter:description', summary);
  setMeta('twitter:image', shareImage || logo);
  setHeadLink('apple-touch-icon', logo);
  setHeadLink('icon', logo, { type: logo?.startsWith('data:') ? '' : 'image/png' });
}

function manifestHrefForSlug(slug) {
  if (
    typeof window !== 'undefined'
    && ['localhost', '127.0.0.1'].includes(window.location.hostname)
    && String(slug || '').toLowerCase() === 'demo'
  ) {
    return '/manifest.json';
  }
  if (String(slug || '').includes('.')) return '/manifest.json';
  return `/api/tenants/public/${encodeURIComponent(slug)}/manifest.json`;
}

function installIconHrefForSlug(slug) {
  return `/api/tenants/public/${encodeURIComponent(slug)}/install-icon`;
}

export default function TenantManifestLink() {
  useEffect(() => {
    let activeController = null;

    const apply = () => {
      if (isAgentsHubSurface()) {
        if (activeController) activeController.abort();
        setManifestHref('/manifest.json');
        applyInstallBrand(AGENTS_HUB_BRAND);
        setHeadLink('apple-touch-icon', `/assets/so-agents/apple-touch-icon.png?v=${SO_AGENT_ICON_VERSION}`);
        return;
      }
      if (isSoAgentHost()) {
        if (activeController) activeController.abort();
        setManifestHref('/assets/so-agents/manifest.json');
        applyInstallBrand(SO_AGENT_BRAND);
        setHeadLink('apple-touch-icon', `/assets/so-agents/apple-touch-icon.png?v=${SO_AGENT_ICON_VERSION}`);
        return;
      }
      const slug = extractSlug(window.location.pathname);
      if (activeController) activeController.abort();
      if (slug) {
        applyInstallBrand({
          name: titleFromSlug(slug),
          logo: installIconHrefForSlug(slug),
          color: '#0B0E14',
        });
        setManifestHref(manifestHrefForSlug(slug));
        activeController = new AbortController();
        fetchTenantBrand(slug, activeController.signal)
          .then(tenant => applyInstallBrand(pickTenantBrand(tenant, slug)))
          .catch((err) => {
            if (err?.name === 'AbortError') return;
            applyInstallBrand({
              name: slug,
              logo: installIconHrefForSlug(slug),
              color: '#0B0E14',
            });
          });
  } else {
        setManifestHref('/manifest.json');
        applyInstallBrand({
          name: 'PlayEMP',
          logo: '/assets/playemp-icon.svg',
          color: '#07090f',
        });
      }
    };
    apply();
    // react-router mutates history without firing popstate, so wrap pushState
    // to detect SPA navigations too.
    const originals = {};
    const wrap = (method) => {
      const orig = history[method];
      originals[method] = orig;
      history[method] = function (...args) {
        const r = orig.apply(this, args);
        apply();
        return r;
      };
    };
    wrap('pushState');
    wrap('replaceState');
    window.addEventListener('popstate', apply);
    return () => {
      if (activeController) activeController.abort();
      Object.entries(originals).forEach(([method, orig]) => {
        history[method] = orig;
      });
      window.removeEventListener('popstate', apply);
    };
  }, []);

  return null;
}
